export const RULE_GROUPS = {
  TASK: "task",
  DOCUMENT: "document",
  SPRINT: "sprint",
  PROJECT: "project",
};

export const RULE_GROUP_META = {
  [RULE_GROUPS.TASK]: {
    label: "Tarefas",
    itemLabel: "Tarefa",
    pill: "dev",
    description: "Status, horas e campos obrigatórios das tarefas",
  },
  [RULE_GROUPS.DOCUMENT]: {
    label: "Documentos",
    itemLabel: "Documento",
    pill: "attention",
    description: "Situação e prazo dos documentos da sprint",
  },
  [RULE_GROUPS.SPRINT]: {
    label: "Sprints",
    itemLabel: "Sprint",
    pill: "maint",
    description: "Abertura, encerramento e uso da Sprint 00",
  },
  [RULE_GROUPS.PROJECT]: {
    label: "Projeto",
    itemLabel: "Projeto",
    pill: "none",
    description: "Lançamento de horas do projeto no período",
  },
};

// Array, e não mapa: a ordem é a precedência do "primeiro match ganha",
// copiada do switch de getFilteredItems para que unificar as cinco cópias
// espalhadas pela tela legada não mude a classificação de nenhuma ocorrência.
// `match` é função porque mandatory-fields é uma disjunção; a guarda de
// descrição nula fica no classificador, não repetida vinte e duas vezes.
export const INCONSISTENCY_RULES = [
  {
    id: "backlog-with-time",
    label: "Tarefas no backlog com tempo registrado",
    group: RULE_GROUPS.TASK,
    statusLabel: "Backlog",
    description: "Tarefas com status backlog que possuem horas registradas.",
    match: (description) => description.includes("Backlog mas possui registros"),
  },
  {
    id: "canceled-with-time",
    label: "Tarefas canceladas com tempo registrado",
    group: RULE_GROUPS.TASK,
    statusLabel: "Cancelada",
    description: "Tarefas canceladas não devem conter horas registradas.",
    match: (description) => description.includes("Cancelada mas possui registros de tempo"),
  },
  {
    id: "returned-with-time",
    label: "Tarefas devolvidas com tempo registrado",
    group: RULE_GROUPS.TASK,
    statusLabel: "Devolvida",
    description: "Tarefas devolvidas não devem conter horas registradas.",
    match: (description) => description.includes("Devolvida mas possui registros de tempo"),
  },
  {
    id: "execution-no-time",
    label: "Tarefas em execução sem tempo registrado",
    group: RULE_GROUPS.TASK,
    statusLabel: "Execução",
    description:
      "Tarefas em andamento sem registro de horas trabalhadas. Pode indicar falha no controle do esforço ou falta de acompanhamento.",
    match: (description) => description.includes("Em execução mas não possui"),
  },
  {
    id: "completed-no-time",
    label: "Tarefas concluídas sem tempo registrado",
    group: RULE_GROUPS.TASK,
    statusLabel: "Concluída",
    description:
      "Tarefas concluídas sem registro de tempo. Onde houve atuação, o registro de horas é obrigatório.",
    match: (description) => description.includes("Concluida mas não possui registros de tempo"),
  },
  {
    id: "no-sprint",
    label: "Tarefas sem sprint atribuída",
    group: RULE_GROUPS.TASK,
    statusLabel: "Sem sprint",
    description:
      "Tarefas ativas sem sprint vinculada, o que dificulta a rastreabilidade e o planejamento do ciclo.",
    match: (description) => description.includes("não tem sprint atribuida"),
  },
  {
    id: "date-out-of-sprint",
    label: "Tarefas com data fora da sprint",
    group: RULE_GROUPS.TASK,
    statusLabel: "Data inválida",
    description:
      "Tarefas atribuídas a uma sprint com data de início ou fim fora do período definido.",
    match: (description) => description.includes("possui data fora"),
  },
  {
    id: "history-issue",
    label: "História com erro",
    group: RULE_GROUPS.TASK,
    statusLabel: "História",
    description:
      "Histórias com dados incompletos. Histórias em execução ou concluídas precisam ter tarefas filhas.",
    match: (description) => description.includes("História:"),
  },
  {
    id: "mandatory-fields",
    label: "Tarefa com campo obrigatório não preenchido",
    group: RULE_GROUPS.TASK,
    statusLabel: "Campo obrigatório",
    description:
      "Tarefas com campos obrigatórios em branco (Módulo, Versão, Plataforma e Ambiente), o que prejudica o acompanhamento e a qualidade da gestão.",
    match: (description) =>
      description.includes("não possui o campo") || description.includes("não possui os campos"),
  },
  {
    id: "no-last-week-hours",
    label: "Projeto sem lançamento na semana anterior",
    group: RULE_GROUPS.PROJECT,
    statusLabel: "Sem lançamento",
    description:
      "Sem horas lançadas, o sistema entende que não houve atuação na sprint, e o apontamento é automático.",
    match: (description) => description.includes("Não houve lançamentos no período:"),
  },
  {
    id: "closed-sprint-inconsistencies",
    label: "Sprints que devem ser fechadas",
    group: RULE_GROUPS.SPRINT,
    statusLabel: "Sprint não encerrada",
    description:
      "Sprints devem ser encerradas ao final do período (S1 até dia 15, S2 até 30 ou 31). Sprint aberta após o prazo gera apontamento.",
    match: (description) =>
      description.includes("está aberta mas já passou do período de encerramento."),
  },
  {
    id: "opened-sprint-inconsistencies",
    label: "Sprint atual deve estar aberta",
    group: RULE_GROUPS.SPRINT,
    statusLabel: "Sem sprint aberta",
    description:
      "Sprints em andamento devem permanecer abertas. Fechada durante o período vigente gera apontamento.",
    match: (description) => description.includes("não possui sprint aberta no mês atual."),
  },
  {
    id: "sprint00-inconsistencies",
    label: "Sprint 00 com lançamentos de horas",
    group: RULE_GROUPS.SPRINT,
    statusLabel: "Sprint 00",
    description: "A Sprint 00 é exclusiva para auditorias e não deve ter lançamento de horas.",
    match: (description) => description.includes("A Sprint 00:"),
  },
  {
    id: "wrong-document-status",
    label: "Documento do Projeto com situação incorreta",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Documento do Projeto",
    description:
      "Deve permanecer em execução durante toda a sprint. Status incorreto antes do encerramento gera inconsistência.",
    match: (description) => description.includes("O Documento do Projeto de id"),
  },
  {
    id: "completed-baseline",
    label: "Baseline da Sprint com situação incorreta",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Baseline",
    description:
      "Deve permanecer em backlog até o último dia da sprint. Concluir antes do encerramento gera inconsistência.",
    match: (description) => description.includes("O Documento Baseline da Sprint de id"),
  },
  {
    id: "wrong-milestone",
    label: "Acompanhamento do Marco da Sprint",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Marco da Sprint",
    description:
      "Deve ser concluído apenas no final da sprint. Conclusão antecipada ou falta de informação gera apontamento.",
    match: (description) =>
      description.includes("O Documento Acompanhamento do Marco da Sprint de id"),
  },
  {
    id: "wrong-planning",
    label: "Planejamento da Sprint",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Planejamento da Sprint",
    description:
      "Deve estar em backlog até o primeiro dia da sprint e ser concluído no próprio dia, com os campos obrigatórios preenchidos.",
    match: (description) => description.includes("O Documento Planejamento da Sprint"),
  },
  {
    id: "wrong-review",
    label: "Review da Sprint",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Review da Sprint",
    description:
      "Deve ser concluída no último dia da sprint e permanecer em backlog até essa data.",
    match: (description) => description.includes("O Documento Review da Sprint"),
  },
  {
    id: "wrong-retrospective",
    label: "Retrospectiva da Sprint",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Retrospectiva da Sprint",
    description:
      "Deve ser concluída no último dia da sprint, com os campos preenchidos e o arquivo anexado.",
    match: (description) => description.includes("O Documento Retrospectiva da Sprint"),
  },
  {
    id: "wrong-lvq",
    label: "Lista de Verificação da Qualidade",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Lista de Verificação da Qualidade",
    description:
      "O checklist de qualidade deve estar completo ao final da sprint. Campos ausentes geram apontamento.",
    match: (description) =>
      description.includes("O Documento LVQS - Lista de Verificação da Qualidade"),
  },
  {
    id: "wrong-verification-validation",
    label: "Verificação e Validação Sprint",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Verificação e Validação Sprint",
    description:
      "É necessário descrever os testes realizados ou justificar a ausência. Concluir apenas no final da sprint.",
    match: (description) => description.includes("O Documento Verificação e Validação Sprint"),
  },
  {
    id: "wrong-followup",
    label: "Acompanhamento da semana",
    group: RULE_GROUPS.DOCUMENT,
    statusLabel: "Acompanhamento da semana",
    description:
      "S1 (1 a 7) e S2 (16 a 22) devem estar em execução dentro do período. Depois disso, devem ser concluídas.",
    match: (description) => description.includes("Acompanhamento da semana"),
  },
];

const RULES_BY_ID = new Map(INCONSISTENCY_RULES.map((rule) => [rule.id, rule]));

export function ruleById(id) {
  return RULES_BY_ID.get(id);
}

export function rulesOfGroup(group) {
  return INCONSISTENCY_RULES.filter((rule) => rule.group === group);
}
